import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Phase 17: Step 4 - Notifications Permission
 * Request push notification permission
 */
function Step4Notifications() {
  const navigate = useNavigate();
  const [permissionStatus, setPermissionStatus] = useState('default'); // 'default', 'granted', 'denied'
  const [loading, setLoading] = useState(false);

  const handleRequestPermission = async () => {
    setLoading(true);
    try {
      if ('Notification' in window) {
        const permission = await Notification.requestPermission();
        setPermissionStatus(permission);
        
        if (permission === 'granted') {
          // Success feedback
          setTimeout(() => {
            navigate('/onboarding/step5');
          }, 1000);
        } else {
          setLoading(false);
        }
      } else {
        alert('Ihr Browser unterstützt keine Push-Benachrichtigungen.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      setLoading(false);
    }
  };

  const handleSkip = () => {
    navigate('/onboarding/step5');
  };

  return (
    <div className="w-full max-w-2xl mx-auto animate-fade-in">
      {/* Progress */}
      <div className="mb-8">
        <div className="flex justify-center gap-2">
          <div className="w-3 h-3 rounded-full bg-white"></div>
          <div className="w-3 h-3 rounded-full bg-white"></div>
          <div className="w-3 h-3 rounded-full bg-white"></div>
          <div className="w-3 h-3 rounded-full bg-white"></div>
          <div className="w-3 h-3 rounded-full bg-white/50"></div>
        </div>
      </div>

      {/* Card */}
      <div className="bg-neutral-900/90 backdrop-blur-xl rounded-3xl border border-neutral-600/40 p-8 sm:p-12 shadow-2xl animate-scale-in">
        {/* Icon with pulse animation */}
        <div className="text-center mb-6 relative">
          <div className="inline-block relative">
            <span className="text-7xl relative z-10">🔔</span>
            {permissionStatus === 'default' && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-24 h-24 rounded-full bg-hayat-warning-400/30 animate-ping"></div>
              </div>
            )}
          </div>
        </div>

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl font-bold text-[#F9FAFB] mb-2 text-center">
          Benachrichtigungen aktivieren
        </h1>
        <p className="text-neutral-500 mb-8 text-center">
          Bleiben Sie immer informiert über wichtige Ereignisse
        </p>

        {/* Status Display */}
        {permissionStatus === 'granted' && (
          <div className="mb-8 p-6 bg-hayat-success-50 border border-hayat-success-200 rounded-2xl text-center animate-scale-in">
            <span className="text-4xl mb-2 block">✅</span>
            <p className="text-hayat-success-700 font-bold">
              Benachrichtigungen aktiviert!
            </p>
          </div>
        )}

        {permissionStatus === 'denied' && (
          <div className="mb-8 p-6 bg-hayat-danger-50 border border-hayat-danger-200 rounded-2xl text-center">
            <span className="text-4xl mb-2 block">❌</span>
            <p className="text-hayat-danger-700 font-bold mb-2">
              Benachrichtigungen blockiert
            </p>
            <p className="text-sm text-hayat-danger-600">
              Sie können die Berechtigung in Ihren Browser-Einstellungen aktivieren.
            </p>
          </div>
        )}

        {permissionStatus === 'default' && (
          <div className="mb-8">
            <div className="space-y-3 text-left">
              <div className="flex items-start gap-3">
                <span className="text-2xl">🚨</span>
                <div>
                  <h3 className="font-bold text-[#F9FAFB]">Notfälle</h3>
                  <p className="text-sm text-neutral-500">Sofortige Benachrichtigung bei Notrufen</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-2xl">⏰</span>
                <div>
                  <h3 className="font-bold text-[#F9FAFB]">Erinnerungen</h3>
                  <p className="text-sm text-neutral-500">Medikamente und Termine</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-2xl">📞</span>
                <div>
                  <h3 className="font-bold text-[#F9FAFB]">Anrufe</h3>
                  <p className="text-sm text-neutral-500">Eingehende Video-Anrufe</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-4">
          <button
            onClick={handleSkip}
            disabled={loading}
            className="flex-1 py-4 bg-hayat-neutral-100 text-[#CBD5F5] rounded-2xl font-semibold hover:bg-hayat-neutral-200 active:scale-95 transition-all duration-300 disabled:opacity-50"
          >
            Später
          </button>
          {permissionStatus !== 'granted' && (
            <button
              onClick={handleRequestPermission}
              disabled={loading || permissionStatus === 'denied'}
              className="flex-1 py-4 bg-gradient-to-r from-hayat-warning-500 to-hayat-warning-600 text-white rounded-2xl font-bold shadow-hayat-lg hover:shadow-hayat-xl hover:scale-105 active:scale-95 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Aktiviere...' : 'Aktivieren 🔔'}
            </button>
          )}
          {permissionStatus === 'granted' && (
            <button
              onClick={() => navigate('/onboarding/step5')}
              className="flex-1 py-4 bg-gradient-to-r from-hayat-primary-500 to-hayat-primary-600 text-white rounded-2xl font-bold shadow-hayat-lg hover:shadow-hayat-xl hover:scale-105 active:scale-95 transition-all duration-300"
            >
              Weiter →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default Step4Notifications;